import type { FC } from 'react';
import { useCallback } from 'react';
import type { ActionIndexProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useRulePageMode from '../../hooks/useRulePageMode';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import actionFieldsVisibility from '../../utils/actionFieldsVisibility';
import { useCurrentNetwork } from '../../../../hooks';
import { Box, Button, Tooltip } from '@mui/material';
import { Percent, Tag as TagIcon } from '@mui/icons-material';

export const ActionUnitValue: FC<ActionIndexProps> = observer(({ indexTask, indexVariant, indexAction }) => {
  const { isRead } = useRulePageMode();
  const tasksFormStore = useCurrentTasksFormStore();
  const storedActionValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.action;
  const { isUnitVisible, isSubActionVisible } = actionFieldsVisibility(storedActionValue as string);
  const network = useCurrentNetwork();

  const storedUnitValue = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant })?.unit;
  const handleActionUnit = useCallback(() => {
    const newUnitValue = storedUnitValue === 'absolute' ? 'percent' : 'absolute';
    tasksFormStore.setActionFieldValue({
      indexTask,
      indexAction,
      indexVariant,
      field: 'unit',
      value: newUnitValue,
    });
  }, [tasksFormStore, storedUnitValue, indexTask, indexVariant, indexAction]);

  if (isRead) return null;

  return (
    (isUnitVisible || network === 'google') && (
      <Box
        sx={{
          flex: '0 0 42px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: isSubActionVisible ? `1px solid ${`rgba(0, 8, 46, 0.12)`}` : 'none',
        }}
      >
        <Tooltip title={storedUnitValue === 'percent' ? 'Percent value' : 'Number value'} placement="top">
          <Box>
            <Button
              onClick={handleActionUnit}
              variant="text"
              sx={(t) => {
                return {
                  '&': {
                    width: 28,
                    maxWidth: 28,
                    minWidth: 28,
                    p: 0,
                    height: 28,
                    borderRadius: t.borderRadius.md,
                    backgroundColor: 'white',
                    border: `1px solid ${t.palette.grey[300]}`,
                    '&:hover': {
                      border: `1px solid ${t.palette.grey[400]}`,
                    },
                  },
                };
              }}
            >
              {storedUnitValue === 'percent' ? (
                <Percent
                  sx={(t) => ({
                    fontSize: t.typography.h3.fontSize,
                    color: t.palette.text.secondary,
                  })}
                />
              ) : (
                <TagIcon
                  sx={(t) => ({
                    fontSize: t.typography.h3.fontSize,
                    color: t.palette.text.secondary,
                  })}
                />
              )}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    )
  );
});

export default ActionUnitValue;
